/**
 * Whether the live reel / result card / compare should show
 * “FICTIONAL RECONSTRUCTION”.
 *
 * Conditions (all required):
 * - an active cast op exists for a slot
 * - selected variant is NOT source-faithful
 * - showReconstructionDisclosure === true
 *
 * Hidden disclosure must NOT display the label (but core scoring applies
 * worse delayed Cred via provenance risk). Source-faithful casts never show it.
 */
import {
  activeOps,
  type EditGraph,
  type ResolvedEdit,
  type SourcePack,
  resolveEdit,
} from "@desk/core";

export const RECONSTRUCTION_LABEL = "FICTIONAL RECONSTRUCTION";

export function shouldShowReconstructionLabel(
  pack: SourcePack,
  graphOrResolved: EditGraph | ResolvedEdit,
): boolean {
  const resolved =
    "casts" in graphOrResolved && graphOrResolved.casts instanceof Map
      ? (graphOrResolved as ResolvedEdit)
      : resolveEdit(graphOrResolved as EditGraph, pack);

  const slots = pack.castSlots ?? [];
  if (slots.length === 0) return false;

  for (const slot of slots) {
    const castOp = resolved.casts.get(slot.id);
    if (!castOp) continue;
    if (!castOp.showReconstructionDisclosure) continue;
    const variant = slot.variants.find((v) => v.id === castOp.variantId);
    if (!variant) continue;
    const faithful = variant.sourceFaithful || variant.id === slot.defaultVariantId;
    if (!faithful) return true;
  }
  return false;
}

/** Active non–source-faithful cast with disclosure hidden — worse Cred path exists in core. */
export function hasHiddenReconstruction(
  pack: SourcePack,
  graph: EditGraph,
): boolean {
  const resolved = resolveEdit(graph, pack);
  const slots = pack.castSlots ?? [];
  for (const slot of slots) {
    const castOp = resolved.casts.get(slot.id);
    if (!castOp) continue;
    if (castOp.showReconstructionDisclosure) continue;
    const variant = slot.variants.find((v) => v.id === castOp.variantId);
    if (!variant) continue;
    const faithful = variant.sourceFaithful || variant.id === slot.defaultVariantId;
    if (!faithful) return true;
  }
  // Also check active ops in case resolve omitted — prefer resolved.
  void activeOps;
  return false;
}
